import { createSlice, createAsyncThunk, createSelector, PayloadAction } from '@reduxjs/toolkit';
import api from '../config/api';
import type { RootState } from '../config/store';
import jwService from '../services/jwService';

// --- TYPE DEFINITIONS ---

interface ErrorType {
  message: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  [key: string]: any;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

// --- ASYNC THUNKS ---

interface LoginPayload {
  email: string;
  password: string;
}

// This thunk is correct as is, because it's the source of the tokens.
export const loginUser = createAsyncThunk<LoginResponse, LoginPayload>(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post<LoginResponse>('/Accounts/Login', credentials);
      localStorage.setItem('token', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Login failed');
    }
  }
);

// This thunk is also correct as is.
export const refreshToken = createAsyncThunk<any, void>(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return rejectWithValue('No refresh token');
    try {
      const response = await api.post('/Accounts/Refresh', { refreshToken });
      localStorage.setItem('token', response.data.accessToken);
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

interface CreateUserPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  role: string;
  metadata: { [key: string]: string; };
}

// ✅ All thunks below are simplified
export const createUser = createAsyncThunk<any, CreateUserPayload>(
  'auth/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      // The interceptor now adds the token automatically
      const response = await api.post('/Accounts', userData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchUserSetting = createAsyncThunk<any, string>(
  'auth/fetchUserSetting',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/Users/${userId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchSelf = createAsyncThunk<User, void>(
  'auth/fetchSelf',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/Accounts/Self');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

interface RoleModificationPayload {
  email: string;
  role: string;
}

export const addRoleToUser = createAsyncThunk<any, RoleModificationPayload>(
  'auth/addRoleToUser',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.put('/Accounts/Role', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const removeRoleFromUser = createAsyncThunk<any, RoleModificationPayload>(
  'auth/removeRoleFromUser',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.delete('/Accounts/Role', { data: payload });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

type fetchUsersPayload = {
  pageSize?: number;
  page?: number;
  sortOrder?: 'ASC' | 'DESC';
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
};

export const fetchUsers = createAsyncThunk<User[], fetchUsersPayload>(
  'auth/fetchUsers',
  async (args: fetchUsersPayload = { pageSize: 1000, page: 1, sortOrder: 'ASC' }, { rejectWithValue }) => {
    try {
      // Convert args to a query string, ensuring all values are strings
      const query = new URLSearchParams(
        Object.entries(args).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = String(value); // Ensure all values are strings
          }
          return acc;
        }, {} as Record<string, string>)
      ).toString();

      const response = await api.get(`/Accounts?${query}`);
      return response.data.items || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred while fetching users');
    }
  }
);

export const fetchAllUsers = createAsyncThunk<User[], { pageSize?: number; sortOrder?: 'ASC' | 'DESC' } | void>(
  'auth/fetchAllUsers',
  async (args, { rejectWithValue }) => {
    try {
      const pageSize = (args as any)?.pageSize ?? 200;
      const sortOrder = (args as any)?.sortOrder ?? 'ASC';

      const allUsers: User[] = [];
      let page = 1;

      // Safety cap to avoid infinite loops if the API ignores paging.
      const maxPages = 50;

      for (let i = 0; i < maxPages; i += 1) {
        const query = new URLSearchParams({
          pageSize: String(pageSize),
          page: String(page),
          sortOrder: String(sortOrder),
        }).toString();

        const response = await api.get(`/Accounts?${query}`);
        const data = response.data;

        // Some endpoints return the array directly.
        if (Array.isArray(data)) {
          return data as User[];
        }

        const items = data?.items || data?.Items || [];
        if (!Array.isArray(items)) {
          return rejectWithValue('Unexpected users response shape');
        }

        allUsers.push(...(items as User[]));

        // Stop when the page returns fewer than requested items.
        if (items.length < pageSize) break;
        page += 1;
      }

      return allUsers;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred while fetching users');
    }
  }
);

interface UserUpdatePayload {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  profilePicture: string;
}

export const updateUser = createAsyncThunk<User, UserUpdatePayload>(
  'users/updateUser',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.put(`/Accounts/${payload.id}`, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

interface CreateDocumentPayload {
  userId: string;
  title: string;
  filePath: string;
  type: number;
}

export const createDocument = createAsyncThunk<any, CreateDocumentPayload>(
  'auth/createDocument',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post(`/Users/${payload.userId}/Documents`, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

interface FetchUserDocumentsPayload {
  userId: string;
}

export const fetchUserDocuments = createAsyncThunk<any[], FetchUserDocumentsPayload>(
  'auth/fetchUserDocuments',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.get(`/Users/${payload.userId}/Documents?pageSize=1000`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

interface DeleteUserDocumentPayload {
  userId: string;
  documentId: string;
}

export const deleteUserDocument = createAsyncThunk<any, DeleteUserDocumentPayload>(
  'auth/deleteUserDocument',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/Users/${payload.userId}/Documents/${payload.documentId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchUserById = createAsyncThunk<User, string>(
  'auth/fetchUserById',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/Accounts/${userId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const deleteUser = createAsyncThunk<string, string>(
  'users/deleteUser',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/Accounts/${id}`);
      return id; // Return the id on success for filtering
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

interface Commissions {
  id: string;
  metadata: { [key: string]: string; };
}

export const userInfo = createAsyncThunk<any, Commissions>(
  'users/userInfo',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.put(`/Users/${payload.id}`, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

interface PasswordModificationPayload {
  email: string;
  password: string;
}

export const resetPassword = createAsyncThunk<any, PasswordModificationPayload>(
  'auth/resetPassword',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.put('/Accounts/Password/Reset', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

// --- INITIAL STATE & SLICE DEFINITION ---

interface AuthState {
  user: User | null;
  self: User | null;
  users: User[];
  userInfo: any[];
  userDocuments: any[];
  token: string | null;
  refreshToken: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: any;
  userById: User | null;
  userSetting: any;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  user: null,
  self: null,
  users: [],
  userInfo: [],
  userDocuments: [],
  token: localStorage.getItem('token') || null,
  refreshToken: localStorage.getItem('refreshToken') || null,
  status: 'idle',
  error: null,
  userById: null,
  userSetting: null,
  isAuthenticated: !!localStorage.getItem('token'),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logoutUser: (state) => {
      state.user = null;
      state.self = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      jwService.cleanToken();
    },
    setToken: (state, action: PayloadAction<{ accessToken: string; refreshToken?: string }>) => {
      state.token = action.payload.accessToken;
      if (action.payload.refreshToken) {
        state.refreshToken = action.payload.refreshToken;
      }
      state.isAuthenticated = !!action.payload.accessToken;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.token = action.payload.accessToken;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.accessToken;
        if (action.payload.refreshToken) {
          state.refreshToken = action.payload.refreshToken;
        }
        state.isAuthenticated = true;
      })
      .addCase(fetchSelf.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.self = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.users = action.payload;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.users = action.payload;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.userById = action.payload;
      })
      .addCase(fetchUserDocuments.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.userDocuments = action.payload;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const userId = action.payload;
        state.users = state.users.filter((user) => user.id !== userId);
      })
      .addMatcher(
        (action) => action.type.endsWith('/pending'),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        (action) => action.type.endsWith('/rejected'),
        (state, action) => {
          state.status = 'failed';
          state.error = action.payload;
        }
      );
  },
});

// --- SELECTORS ---

const selectAuthState = (state: RootState) => state.auth;

export const selectUsers = createSelector([selectAuthState], (auth) => auth.users);

export const selectActiveUsers = createSelector([selectUsers], (users) =>
  users.filter((user) => user.isActive)
);

export const { logoutUser, setToken } = authSlice.actions;
export default authSlice.reducer;
